import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Tabs,
  Input,
  Radio,
  Upload,
  Button,
  Space,
  message,
} from "antd";
import { CloudUploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { RestaurantPageContent } from "../RestaurantPOS";

interface EditWebsiteContentFormProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (
    values: RestaurantPageContent,
    files: {
      menu_hero_image?: File;
      menu_feature_image?: File;
      restaurant_hero_image?: File;
    }
  ) => Promise<void>;
  initialValues: RestaurantPageContent | null;
  loading: boolean;
}

export const EditWebsiteContentForm: React.FC<EditWebsiteContentFormProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  loading,
}) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("hero");

  // Media states
  const [menuHeroMode, setMenuHeroMode] = useState<"upload" | "url">("upload");
  const [menuHeroFile, setMenuHeroFile] = useState<File | null>(null);

  const [menuFeatureMode, setMenuFeatureMode] = useState<"upload" | "url">("upload");
  const [menuFeatureFile, setMenuFeatureFile] = useState<File | null>(null);

  const [restaurantHeroMode, setRestaurantHeroMode] = useState<"upload" | "url">("upload");
  const [restaurantHeroFile, setRestaurantHeroFile] = useState<File | null>(null);

  // Watch URLs for previews
  const menuHeroUrlValue = Form.useWatch("menu_hero_image", form);
  const menuFeatureUrlValue = Form.useWatch("menu_feature_image", form);
  const restaurantHeroUrlValue = Form.useWatch("restaurant_hero_image", form);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
        setMenuHeroMode(initialValues.menu_hero_image ? "url" : "upload");
        setMenuFeatureMode(initialValues.menu_feature_image ? "url" : "upload");
        setRestaurantHeroMode(initialValues.restaurant_hero_image ? "url" : "upload");
      } else {
        form.resetFields();
        setMenuHeroMode("upload");
        setMenuFeatureMode("upload");
        setRestaurantHeroMode("upload");
      }
      setActiveTab("hero");
      setMenuHeroFile(null);
      setMenuFeatureFile(null);
      setRestaurantHeroFile(null);
    }
  }, [visible, initialValues, form]);

  const handleModeChange = (
    nextMode: "upload" | "url",
    currentMode: "upload" | "url",
    file: File | null,
    urlValue: string | undefined,
    setMode: (mode: "upload" | "url") => void
  ) => {
    if (currentMode === "upload" && nextMode === "url" && file) {
      message.warning("Please remove the uploaded file first before providing a URL.");
      return;
    }
    if (currentMode === "url" && nextMode === "upload" && urlValue) {
      message.warning("Please clear the image URL first before uploading a file.");
      return;
    }
    setMode(nextMode);
  };

  const onFinish = async (values: any) => {
    await onSubmit(values as RestaurantPageContent, {
      menu_hero_image: menuHeroFile || undefined,
      menu_feature_image: menuFeatureFile || undefined,
      restaurant_hero_image: restaurantHeroFile || undefined,
    });
  };

  const renderImageUploadSection = (
    label: string,
    fieldName: string,
    mode: "upload" | "url",
    setMode: (mode: "upload" | "url") => void,
    file: File | null,
    setFile: (file: File | null) => void,
    urlValue: string | undefined
  ) => {
    return (
      <div style={{ marginBottom: 24, padding: 16, border: "1px solid #f0f0f0", borderRadius: 8 }}>
        <h4 style={{ marginBottom: 16 }}>{label}</h4>
        <Form.Item label="Image Source" style={{ marginBottom: 16 }}>
          <Radio.Group
            value={mode}
            onChange={(e) => handleModeChange(e.target.value, mode, file, urlValue, setMode)}
            optionType="button"
            buttonStyle="solid"
            style={{ width: "100%", display: "flex" }}
          >
            <Radio.Button value="upload" style={{ flex: 1, textAlign: "center" }}>
              Upload File
            </Radio.Button>
            <Radio.Button value="url" style={{ flex: 1, textAlign: "center" }}>
              Provide URL
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {mode === "upload" ? (
          <Form.Item label="Upload Image">
            <Upload.Dragger
              name={fieldName}
              multiple={false}
              accept="image/*"
              beforeUpload={(f) => {
                setFile(f);
                form.setFieldValue(fieldName, undefined);
                return false;
              }}
              onRemove={() => setFile(null)}
              fileList={file ? [file as any] : []}
              itemRender={(originNode, f, fileList, actions) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "8px",
                    marginTop: "8px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "70%",
                    }}
                  >
                    {f.name}
                  </span>
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={actions.remove}>
                    Remove
                  </Button>
                </div>
              )}
            >
              <p className="ant-upload-drag-icon">
                <CloudUploadOutlined />
              </p>
              <p className="ant-upload-text">Choose a file or drag & drop it here</p>
              <p className="ant-upload-hint">JPEG, PNG, SVG formats, up to 10MB</p>
            </Upload.Dragger>
          </Form.Item>
        ) : (
          <Form.Item name={fieldName} label="Image URL">
            <Input placeholder="https://..." allowClear />
          </Form.Item>
        )}

        {(file || urlValue) && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <p style={{ marginBottom: 8, color: "#8c8c8c" }}>Image Preview</p>
            <img
              src={file ? URL.createObjectURL(file) : urlValue}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: 200,
                objectFit: "contain",
                borderRadius: 8,
                border: "1px solid #f0f0f0",
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const tabsItems = [
    {
      key: "hero",
      label: "Hero Sections",
      children: (
        <div style={{ paddingTop: 16 }}>
          <h4>Menu Hero</h4>
          <Form.Item name="menu_hero_eyebrow" label="Eyebrow">
            <Input />
          </Form.Item>
          <Form.Item name="menu_hero_title" label="Title">
            <Input />
          </Form.Item>
          <Form.Item name="menu_hero_subtitle" label="Subtitle">
            <Input />
          </Form.Item>

          <h4 style={{ marginTop: 32 }}>Restaurant Hero</h4>
          <Form.Item name="restaurant_hero_eyebrow" label="Eyebrow">
            <Input />
          </Form.Item>
          <Form.Item name="restaurant_hero_title" label="Title">
            <Input />
          </Form.Item>
          <Form.Item name="restaurant_hero_subtitle" label="Subtitle">
            <Input />
          </Form.Item>
        </div>
      ),
    },
    {
      key: "sections",
      label: "Intro & Sections",
      children: (
        <div style={{ paddingTop: 16 }}>
          <h4>Intro</h4>
          <Form.Item name="restaurant_intro_kicker" label="Kicker">
            <Input />
          </Form.Item>
          <Form.Item name="restaurant_intro_title" label="Title">
            <Input />
          </Form.Item>
          <Form.Item name="restaurant_intro_paragraph_one" label="Paragraph 1">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="restaurant_intro_paragraph_two" label="Paragraph 2">
            <Input.TextArea rows={3} />
          </Form.Item>

          <h4 style={{ marginTop: 32 }}>Featured Section</h4>
          <Form.Item name="featured_section_kicker" label="Kicker">
            <Input />
          </Form.Item>
          <Form.Item name="featured_section_title" label="Title">
            <Input />
          </Form.Item>

          <h4 style={{ marginTop: 32 }}>Experiences Section</h4>
          <Form.Item name="experiences_section_kicker" label="Kicker">
            <Input />
          </Form.Item>
          <Form.Item name="experiences_section_title" label="Title">
            <Input />
          </Form.Item>

          <h4 style={{ marginTop: 32 }}>Reservation Section</h4>
          <Form.Item name="menu_reservation_kicker" label="Kicker">
            <Input />
          </Form.Item>
          <Form.Item name="menu_reservation_title" label="Title">
            <Input />
          </Form.Item>
          <Form.Item name="menu_reservation_description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </div>
      ),
    },
    {
      key: "dynamic",
      label: "Dynamic Content",
      children: (
        <div style={{ paddingTop: 16 }}>
          <Form.List name="opening_hours">
            {(fields, { add, remove }) => (
              <div>
                <h4>Opening Hours</h4>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
                    <Form.Item
                      {...field}
                      name={[field.name, "meal"]}
                      rules={[{ required: true, message: "Meal is required" }]}
                    >
                      <Input placeholder="Meal" />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "hours"]}
                      rules={[{ required: true, message: "Hours are required" }]}
                    >
                      <Input placeholder="Hours" />
                    </Form.Item>
                    <Button onClick={() => remove(field.name)}>Remove</Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ meal: "", hours: "" })} block>
                  Add Opening Hour
                </Button>
              </div>
            )}
          </Form.List>

          <Form.List name="dining_experiences">
            {(fields, { add, remove }) => (
              <div style={{ marginTop: 32 }}>
                <h4>Dining Experiences</h4>
                {fields.map((field) => (
                  <div
                    key={field.key}
                    style={{
                      marginBottom: 12,
                      padding: 12,
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                    }}
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, "name"]}
                      label="Name"
                      rules={[{ required: true, message: "Name is required" }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "icon"]}
                      label="Icon"
                      rules={[{ required: true, message: "Icon is required" }]}
                    >
                      <Input placeholder="e.g. 🌅" />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "description"]}
                      label="Description"
                      rules={[{ required: true, message: "Description is required" }]}
                    >
                      <Input.TextArea rows={2} />
                    </Form.Item>
                    <Button onClick={() => remove(field.name)} danger>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add({ name: "", icon: "", description: "" })} block>
                  Add Dining Experience
                </Button>
              </div>
            )}
          </Form.List>
        </div>
      ),
    },
    {
      key: "media",
      label: "Media",
      children: (
        <div style={{ paddingTop: 16 }}>
          {renderImageUploadSection(
            "Menu Hero Image",
            "menu_hero_image",
            menuHeroMode,
            setMenuHeroMode,
            menuHeroFile,
            setMenuHeroFile,
            menuHeroUrlValue
          )}
          {renderImageUploadSection(
            "Menu Feature Image",
            "menu_feature_image",
            menuFeatureMode,
            setMenuFeatureMode,
            menuFeatureFile,
            setMenuFeatureFile,
            menuFeatureUrlValue
          )}
          {renderImageUploadSection(
            "Restaurant Hero Image",
            "restaurant_hero_image",
            restaurantHeroMode,
            setRestaurantHeroMode,
            restaurantHeroFile,
            setRestaurantHeroFile,
            restaurantHeroUrlValue
          )}
        </div>
      ),
    },
    {
      key: "cta",
      label: "CTA Settings",
      children: (
        <div style={{ paddingTop: 16 }}>
          <Form.Item name="cta_title" label="CTA Title">
            <Input />
          </Form.Item>
          <Form.Item name="cta_description" label="CTA Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="cta_button_text" label="CTA Button Text">
            <Input />
          </Form.Item>
          <Form.Item name="cta_button_link" label="CTA Button Link">
            <Input />
          </Form.Item>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="Edit Restaurant Website Content"
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)} items={tabsItems} />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          {activeTab === "hero" && (
            <>
              <Button onClick={onCancel}>Cancel</Button>
              <Button type="primary" onClick={() => setActiveTab("sections")}>
                Next
              </Button>
            </>
          )}
          {activeTab === "sections" && (
            <>
              <Button onClick={() => setActiveTab("hero")}>Back</Button>
              <Button type="primary" onClick={() => setActiveTab("dynamic")}>
                Next
              </Button>
            </>
          )}
          {activeTab === "dynamic" && (
            <>
              <Button onClick={() => setActiveTab("sections")}>Back</Button>
              <Button type="primary" onClick={() => setActiveTab("media")}>
                Next
              </Button>
            </>
          )}
          {activeTab === "media" && (
            <>
              <Button onClick={() => setActiveTab("dynamic")}>Back</Button>
              <Button type="primary" onClick={() => setActiveTab("cta")}>
                Next
              </Button>
            </>
          )}
          {activeTab === "cta" && (
            <>
              <Button onClick={() => setActiveTab("media")}>Back</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Changes
              </Button>
            </>
          )}
        </div>
      </Form>
    </Modal>
  );
};
